import { AdminService, htmlUnescape } from '@naturalcycles/js-lib'
import { languageContext } from '@src/context/language.context'
import { useNonDevExperimentImpression } from '@src/hooks/useNonDevExperimentImpression'
import { usePageNameClass } from '@src/hooks/usePageNameClass'
import { ConsumerPageTypes, PageType, usePageType } from '@src/hooks/usePageType'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { ClientOnly } from '../client-only/ClientOnly.component'
import { Footer } from '../footer/Footer.component'
import { GlobalSeo } from '../global-seo/GlobalSeo.component'
import { Header } from '../header/Header.component'
import styles from './Page.module.scss'
const QaMenu = lazy(() => import('../qaMenu/QaMenu.component').then(m => ({ default: m.QaMenu })))
import { qaContext } from '@src/context/qa.context'
import { PageContextWithDefaults } from '@src/helpers/gatsby/pages'

export interface PageProps {
  children: ReactNode
  title?: string
  extraBodyClass?: string
  noFooter?: boolean
  noHeader?: boolean
  noDiscountBanner?: boolean
  pageContext: PageContextWithDefaults
}

export function Page({
  children,
  title = '',
  extraBodyClass = '',
  noFooter = false,
  noHeader = false,
  noDiscountBanner = false,
  pageContext: {
    _site: { globalSeo },
    products,
    menu,
    sitemap,
  },
}: PageProps): ReactNode {
  const pageType = usePageType()
  const pageSlug = usePageNameClass()
  const { locale } = useContext(languageContext)
  useNonDevExperimentImpression()

  const bodyClasses = classNames(pageSlug, extraBodyClass, {
    'nc-consumer': [...ConsumerPageTypes].includes(pageType),
  })

  const mainClassNames = classNames({
    [styles.Page__main]: [PageType.Campaign].includes(pageType),
  })

  const [displayMenu, setDisplayMenu] = useState(false)
  const [adminService] = useState(
    new AdminService({
      onRedDotClick: () => {
        setDisplayMenu(prev => !prev)
      },
    }),
  )

  useEffect(() => {
    adminService.startListening()
    return () => adminService.stopListening()
  }, [])

  async function toggleRedDot(): Promise<void> {
    await adminService.toggleRedDot()
  }

  return (
    <>
      <Helmet>
        {/* eslint-disable-next-line react/no-unknown-property */}
        <html lang={locale} prefix="og: http://ogp.me/ns#" />
        <title>{htmlUnescape(title)}</title>
        <body className={bodyClasses} />
      </Helmet>

      <GlobalSeo globalSeo={globalSeo}>
        <qaContext.Provider value={{ toggleRedDot }}>
          {!noHeader && (
            <Header canHaveDiscountBanner={!noDiscountBanner} products={products} menu={menu} />
          )}
          <ClientOnly>
            <Suspense>{displayMenu && <QaMenu />}</Suspense>
          </ClientOnly>
          {/* Dynamic Template manages the footer from Dato */}
          <main className={mainClassNames}>{children}</main>
          {!noFooter && <Footer sitemap={sitemap} />}
        </qaContext.Provider>
      </GlobalSeo>
    </>
  )
}
